import type React from "react";

import { Button } from "@bcc/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@bcc/components/ui/card";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@bcc/components/ui/select";
import { Switch } from "@bcc/components/ui/switch";
import { useAuth } from "@bcc/contexts/auth-context";
import { useToast } from "@bcc/hooks/use-toast";
import { Upload, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  setPreferencesViewBillingAddressCardTestId,
  setPreferencesViewBillingAddressDescriptionTestId,
  setPreferencesViewBillingAddressTitleTestId,
  setPreferencesViewChooseFileButtonTestId,
  setPreferencesViewDescriptionTestId,
  setPreferencesViewPersonaCardTestId,
  setPreferencesViewPersonaDescriptionTestId,
  setPreferencesViewPersonaTitleTestId,
  setPreferencesViewProfileInfoCardTestId,
  setPreferencesViewProfileInfoDescriptionTestId,
  setPreferencesViewProfileInfoTitleTestId,
  setPreferencesViewRoasterInfoCardTestId,
  setPreferencesViewRoasterInfoDescriptionTestId,
  setPreferencesViewRoasterInfoTitleTestId,
  setPreferencesViewSameAsBillingToggleTestId,
  setPreferencesViewSaveChangesButtonTestId,
  setPreferencesViewShippingAddressCardTestId,
  setPreferencesViewShippingAddressDescriptionTestId,
  setPreferencesViewShippingAddressTitleTestId,
  setPreferencesViewTestId,
  setPreferencesViewTitleTestId,
} from "./preferences-view.utils";

interface PreferencesViewProps {
  testId?: string;
}

export function PreferencesView({ testId }: PreferencesViewProps = {}) {
  const { roaster } = useAuth();
  const { t } = useTranslation();
  const [logoPreview, setLogoPreview] = useState<string | null>(
    roaster?.roasterLogoUrl || null,
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [personaType, setPersonaType] = useState("BLPRoaster");
  const { toast } = useToast();

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      setUploadError("File size must be less than 1MB");
      return;
    }

    // Validate file type
    if (!file.type.match(/^image\/(png|svg\+xml|jpeg|jpg)$/)) {
      setUploadError("Only PNG, SVG, and JPG files are supported");
      return;
    }

    setUploadError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setLogoPreview(result);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveLogo = () => {
    if (logoPreview) {
      // In a real app, this would update the roaster logo via API
      alert("Logo updated successfully! Refresh to see changes in header.");
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
  };

  const handlePersonaChange = (persona: string) => {
    setPersonaType(persona);
    toast({
      title: "Persona updated",
      description: `Persona changed to ${persona}. Refresh to see changes.`,
    });
  };

  return (
    <div
      data-testid={testId ?? setPreferencesViewTestId("container")}
      className="space-y-6"
    >
      <div>
        <h1
          data-testid={setPreferencesViewTitleTestId()}
          className="text-3xl font-bold tracking-tight text-balance"
        >
          {t("views.preferences.title")}
        </h1>
        <p
          data-testid={setPreferencesViewDescriptionTestId()}
          className="mt-2 text-muted-foreground"
        >
          {t("views.preferences.description")}
        </p>
      </div>

      <div className="grid gap-6">
        <Card data-testid={setPreferencesViewRoasterInfoCardTestId()}>
          <CardHeader>
            <CardTitle data-testid={setPreferencesViewRoasterInfoTitleTestId()}>
              {t("views.preferences.roasterInfo.title")}
            </CardTitle>
            <CardDescription
              data-testid={setPreferencesViewRoasterInfoDescriptionTestId()}
            >
              {t("views.preferences.roasterInfo.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Logo Upload */}
            <div className="grid gap-4">
              <Label>{t("views.preferences.roasterInfo.logoLabel")}</Label>
              <div className="flex items-start gap-4">
                {/* Logo Preview */}
                <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-border-subtle bg-muted">
                  {logoPreview ? (
                    <div className="relative h-full w-full p-2">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="h-full w-full object-contain"
                      />
                    </div>
                  ) : (
                    <Upload className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1 space-y-2">
                  <Label htmlFor="logo-upload" className="cursor-pointer">
                    <div
                      data-testid={setPreferencesViewChooseFileButtonTestId()}
                      className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                    >
                      {t("views.preferences.roasterInfo.chooseFile")}
                    </div>
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/png,image/svg+xml,image/jpeg"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t("views.preferences.roasterInfo.logoHint")}
                  </p>
                  {uploadError && (
                    <p className="text-xs text-destructive">{uploadError}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSaveLogo}
                  disabled={!logoPreview}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Save Logo
                </Button>
                {logoPreview && (
                  <Button variant="outline" onClick={handleRemoveLogo}>
                    <X className="mr-2 h-4 w-4" />
                    Remove Logo
                  </Button>
                )}
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="roaster-name">Roaster Name (Optional)</Label>
              <Input
                id="roaster-name"
                defaultValue={roaster?.roasterName}
                placeholder="Enter roaster name"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="roaster-phone">
                Roaster Phone Number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="roaster-phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                required
              />
            </div>
          </CardContent>
        </Card>

        <Card data-testid={setPreferencesViewBillingAddressCardTestId()}>
          <CardHeader>
            <CardTitle
              data-testid={setPreferencesViewBillingAddressTitleTestId()}
            >
              Billing Address
            </CardTitle>
            <CardDescription
              data-testid={setPreferencesViewBillingAddressDescriptionTestId()}
            >
              Enter your billing address details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="billing-street">
                Street Address <span className="text-destructive">*</span>
              </Label>
              <Input id="billing-street" placeholder="123 Main St" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="billing-city">
                  City <span className="text-destructive">*</span>
                </Label>
                <Input id="billing-city" placeholder="New York" required />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="billing-state">
                  State/Province <span className="text-destructive">*</span>
                </Label>
                <Input id="billing-state" placeholder="NY" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="billing-zip">
                  ZIP/Postal Code <span className="text-destructive">*</span>
                </Label>
                <Input id="billing-zip" placeholder="10001" required />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="billing-country">
                  Country <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="billing-country"
                  placeholder="United States"
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid={setPreferencesViewShippingAddressCardTestId()}>
          <CardHeader>
            <CardTitle
              data-testid={setPreferencesViewShippingAddressTitleTestId()}
            >
              Shipping Address
            </CardTitle>
            <CardDescription
              data-testid={setPreferencesViewShippingAddressDescriptionTestId()}
            >
              Enter your shipping address details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="same-as-billing">Same as Billing Address</Label>
              <Switch
                id="same-as-billing"
                data-testid={setPreferencesViewSameAsBillingToggleTestId()}
                checked={sameAsBilling}
                onCheckedChange={setSameAsBilling}
              />
            </div>

            {!sameAsBilling && (
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="shipping-street">
                    Street Address <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="shipping-street"
                    placeholder="123 Main St"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="shipping-city">
                      City <span className="text-destructive">*</span>
                    </Label>
                    <Input id="shipping-city" placeholder="New York" required />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="shipping-state">
                      State/Province <span className="text-destructive">*</span>
                    </Label>
                    <Input id="shipping-state" placeholder="NY" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="shipping-zip">
                      ZIP/Postal Code{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input id="shipping-zip" placeholder="10001" required />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="shipping-country">
                      Country <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="shipping-country"
                      placeholder="United States"
                      required
                    />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid={setPreferencesViewProfileInfoCardTestId()}>
          <CardHeader>
            <CardTitle data-testid={setPreferencesViewProfileInfoTitleTestId()}>
              Profile Information
            </CardTitle>
            <CardDescription
              data-testid={setPreferencesViewProfileInfoDescriptionTestId()}
            >
              Update your personal details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="first-name">
                  First Name <span className="text-destructive">*</span>
                </Label>
                <Input id="first-name" defaultValue="James" required />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="last-name">
                  Last Name <span className="text-destructive">*</span>
                </Label>
                <Input id="last-name" defaultValue="Carter" required />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                defaultValue="james@beanz.com"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role">Role</Label>
              <Input id="role" defaultValue="Admin" disabled />
            </div>
            <Button
              data-testid={setPreferencesViewSaveChangesButtonTestId()}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save Changes
            </Button>
          </CardContent>
        </Card>

        <Card data-testid={setPreferencesViewPersonaCardTestId()}>
          <CardHeader>
            <CardTitle data-testid={setPreferencesViewPersonaTitleTestId()}>
              Persona Type (Testing)
            </CardTitle>
            <CardDescription
              data-testid={setPreferencesViewPersonaDescriptionTestId()}
            >
              Change persona to test different user experiences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="persona-type">
                Current Persona: {personaType}
              </Label>
              <Select value={personaType} onValueChange={handlePersonaChange}>
                <SelectTrigger id="persona-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NewRoaster">
                    New Roaster (triggers onboarding)
                  </SelectItem>
                  <SelectItem value="BLPRoaster">BLP Roaster</SelectItem>
                  <SelectItem value="SSRoaster">SS Roaster</SelectItem>
                  <SelectItem value="BCOGRoaster">BC OG Roaster</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {personaType === "NewRoaster" && (
              <p className="text-sm text-muted-foreground">
                Onboarding Status: Incomplete
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
